import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowClockwise,
  ArrowLeft,
  FunnelSimple,
  SlidersHorizontal,
  Warning,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  criterioToParams,
  ICriterioFechamento,
  useCriterioFechamento,
} from '../../../../hooks/useCriterioFechamento';
import {
  IFechamentoTotalizadores,
  IRegimeStats,
  RegimeChave,
  useFechamentos,
} from '../../../../hooks/useFechamentos';
import {
  REGIMES_DISPONIVEIS,
  REGIMES_INCLUIDOS_DEFAULT,
  regimesIncluidosToParams,
  useRegimesIncluidos,
} from '../../../../hooks/useRegimesIncluidos';
import {
  CalendarioFechamentos,
  countActiveFilters,
  CriterioCalculoCard,
  CriterioCalculoModal,
  DEFAULT_FILTERS,
  EmpresaFechamentoModal,
  EmpresasFechamentoTable,
  filtersToParams,
  FiltersDrawer,
  ICelulaSelecionada,
  IFechamentoFilters,
  InfoPopover,
} from './components';
import { formatDays, MESES_FULL } from './helpers';

const formatPercent = (n: number): string => `${n.toFixed(1)}%`;

/** Cor da % em dia: verde >=80%, laranja >=50%, vermelho <50%. */
const getPctColor = (pct: number): string => {
  if (pct >= 80) return '#22C55E';
  if (pct >= 50) return '#F59E0B';
  return '#EF4444';
};

const PEND_COLOR = '#EF4444';

// ============================================================================
// Wrappers de animacao
// ============================================================================

interface ScrollFadeProps {
  children: React.ReactNode;
  delay?: number;
}

/** Fade + slide-up ao entrar na viewport (estilo Apple). */
const ScrollFade = ({ children, delay = 0 }: ScrollFadeProps) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

// ============================================================================
// Quick filter (clicar num KPI aplica filtro no calendario/tabela)
// Estado e regime sao independentes — podem coexistir.
// ============================================================================

type EstadoFiltro = 'ativas' | 'inativas' | null;
interface RegimeFiltro {
  chave: RegimeChave;
  valor: string;
}

export interface QuickFilterState {
  estado: EstadoFiltro;
  regime: RegimeFiltro | null;
  semDados: boolean;
}

const EMPTY_QUICK_FILTER: QuickFilterState = { estado: null, regime: null, semDados: false };

const hasAnyQuickFilter = (q: QuickFilterState): boolean =>
  q.estado !== null || q.regime !== null || q.semDados;

const labelEstado = (e: EstadoFiltro): string => {
  if (e === 'ativas') return 'Apenas ativas';
  if (e === 'inativas') return 'Apenas inativas';
  return '';
};

// ============================================================================
// Stat compacto (visao geral)
// ============================================================================

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  valueColor?: string;
  info?: { title: string; content: React.ReactNode };
  delay?: number;
  isSelected?: boolean;
  onClick?: () => void;
}

const StatTile = ({
  label,
  value,
  hint,
  valueColor,
  info,
  delay = 0,
  isSelected,
  onClick,
}: StatTileProps) => {
  const isClickable = !!onClick;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <GlassCard
        intensity="strong"
        hoverable={isClickable}
        p={6}
        h="full"
        cursor={isClickable ? 'pointer' : 'default'}
        onClick={isClickable ? onClick : undefined}
        borderColor={isSelected ? 'rgba(27, 136, 245, 0.55)' : undefined}
        boxShadow={
          isSelected
            ? '0 1px 0 0 rgba(255, 255, 255, 1) inset, 0 0 0 2px rgba(27, 136, 245, 0.18), 0 12px 32px -8px rgba(27, 136, 245, 0.22)'
            : undefined
        }
      >
        <Flex align="center" gap={1} mb={2}>
          <Text
            fontSize="11px"
            color="#71717A"
            textTransform="uppercase"
            letterSpacing="0.08em"
            fontWeight="semibold"
          >
            {label}
          </Text>
          {info && <InfoPopover title={info.title}>{info.content}</InfoPopover>}
        </Flex>
        <Text
          fontSize={{ base: '28px', md: '34px' }}
          fontWeight="700"
          color={valueColor ?? '#0F172A'}
          letterSpacing="-0.02em"
          lineHeight="1.05"
        >
          {value}
        </Text>
        {hint && (
          <Text fontSize="xs" color="#94A3B8" mt={2}>
            {hint}
          </Text>
        )}
      </GlassCard>
    </motion.div>
  );
};

// ============================================================================
// Numero grande do card no formato "X / Y" (com_dados / total_categoria)
// ============================================================================

interface ValueOverTotalProps {
  value: number;
  total: number;
  /** Cor do valor principal (X). O denominador (Y) sempre cinza. */
  color?: string;
}

const ValueOverTotal = ({ value, total, color }: ValueOverTotalProps) => (
  <>
    <Text as="span" color={color}>
      {value}
    </Text>
    <Text
      as="span"
      fontSize={{ base: '18px', md: '22px' }}
      color="#CBD5E1"
      fontWeight="500"
      ml={1.5}
    >
      / {total}
    </Text>
  </>
);

// ============================================================================
// Hint padrao "X em dia · Y pend." (usado nos 4 cards da Visao geral)
// ============================================================================

interface BreakdownHintProps {
  okLabel: string; // ex: "em dia" | "encerradas"
  okValue: number;
  pendValue: number;
  pendLabel?: string;
}

const BreakdownHint = ({
  okLabel,
  okValue,
  pendValue,
  pendLabel = 'pend.',
}: BreakdownHintProps) => (
  <Flex gap={1.5} align="baseline" flexWrap="wrap">
    <Text as="span" fontWeight="700" color="#22C55E">
      {okValue}
    </Text>
    <Text as="span" color="#94A3B8" fontSize="11px">
      {okLabel}
    </Text>
    <Text as="span" color="#94A3B8" fontSize="11px">
      ·
    </Text>
    <Text as="span" color={PEND_COLOR} fontWeight="600">
      {pendValue}
    </Text>
    <Text as="span" color="#94A3B8" fontSize="11px">
      {pendLabel}
    </Text>
  </Flex>
);

// ============================================================================
// Conteudo padrao do InfoPopover dos cards da Visao geral
// (descricao + linhas com valor/% + total)
// ============================================================================

interface BreakdownLinha {
  label: string;
  valor: number;
  cor: string;
}

interface CardInfoBreakdownProps {
  descricao: React.ReactNode;
  linhas: BreakdownLinha[];
  total: number;
}

const CardInfoBreakdown = ({ descricao, linhas, total }: CardInfoBreakdownProps) => (
  <Stack spacing={2.5}>
    <Text fontSize="xs" color="gray.700" lineHeight="1.5">
      {descricao}
    </Text>
    <Stack spacing={1.5} pt={1} borderTop="1px solid" borderColor="gray.100">
      {linhas.map(l => {
        const pct = total > 0 ? (100 * l.valor) / total : 0;
        return (
          <Flex key={l.label} align="center" gap={2} pt={1.5}>
            <Box w="8px" h="8px" borderRadius="full" bg={l.cor} flexShrink={0} />
            <Text fontSize="xs" color="gray.700" flex={1}>
              {l.label}
            </Text>
            <Text fontSize="xs" fontWeight="700" color="gray.900">
              {l.valor}
            </Text>
            <Text fontSize="10px" color="gray.400" minW="36px" textAlign="right">
              {pct.toFixed(1)}%
            </Text>
          </Flex>
        );
      })}
    </Stack>
    <Flex
      justify="space-between"
      pt={2}
      borderTop="1px solid"
      borderColor="gray.100"
      fontSize="10px"
      color="gray.500"
      textTransform="uppercase"
      letterSpacing="0.06em"
      fontWeight="600"
    >
      <Text>Total</Text>
      <Text>{total}</Text>
    </Flex>
  </Stack>
);

// ============================================================================
// Card de regime
// ============================================================================

const RegimeCard = ({
  stat,
  delay,
  isSelected,
  onClick,
}: {
  stat: IRegimeStats;
  delay: number;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const okColor = getPctColor(stat.pct_ok);
  // Schema novo (com `sem_dados`/`pendente`/etc) chega em 2026-05. Detecta a
  // ausencia da chave (nao a do `?? 0`) pra lidar com deploy desincronizado:
  // se backend ainda esta na versao antiga, evita popover com numeros
  // visivelmente errados ("X de X com fechamento" + breakdown todo zerado).
  const hasNewSchema = stat.sem_dados !== undefined;
  const semDados = stat.sem_dados ?? 0;
  const pendente = stat.pendente ?? 0;
  const encerramentoPendente = stat.encerramento_pendente ?? 0;
  const ativas = stat.ativas ?? 0;
  const inativas = stat.inativas ?? 0;
  // "Com dados" = total da categoria menos sem_dados (mesmo conceito da Visao
  // Geral). So faz sentido com schema novo — sem `sem_dados`, mostrar
  // `total / total` daria a impressao errada de que todas tem fechamento.
  const comDados = stat.total - semDados;
  const temAlgumStatus = stat.ok + stat.pendentes > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <GlassCard
        intensity="strong"
        hoverable
        p={5}
        h="full"
        cursor="pointer"
        onClick={onClick}
        borderColor={isSelected ? 'rgba(27, 136, 245, 0.55)' : undefined}
        boxShadow={
          isSelected
            ? '0 1px 0 0 rgba(255, 255, 255, 1) inset, 0 0 0 2px rgba(27, 136, 245, 0.18), 0 12px 32px -8px rgba(27, 136, 245, 0.22)'
            : undefined
        }
      >
        {/* Label + InfoPopover (mesmo padrao do StatTile da Visao Geral) */}
        <Flex align="center" gap={1} mb={2}>
          <Text
            fontSize="11px"
            color="#71717A"
            textTransform="uppercase"
            letterSpacing="0.08em"
            fontWeight="semibold"
          >
            {stat.label}
          </Text>
          {/* stopPropagation pra que clicar no "i" nao acione o quick filter
              do regime (UX igual a Visao Geral). */}
          <Box onClick={ev => ev.stopPropagation()}>
            <InfoPopover title={stat.label}>
              {hasNewSchema ? (
                <>
                  <CardInfoBreakdown
                    descricao={
                      <>
                        <Text as="span" fontWeight="700">
                          {comDados}
                        </Text>{' '}
                        de{' '}
                        <Text as="span" fontWeight="700">
                          {stat.total}
                        </Text>{' '}
                        empresas têm fechamento contábil registrado (
                        <Text as="span" fontWeight="700" color={okColor}>
                          {formatPercent(stat.pct_ok)}
                        </Text>{' '}
                        em dia). Distribuição segundo o critério atual:
                      </>
                    }
                    linhas={[
                      { label: 'Em dia', valor: stat.ok, cor: '#22C55E' },
                      {
                        label: 'Pendentes (ativas)',
                        valor: pendente,
                        cor: PEND_COLOR,
                      },
                      {
                        label: 'Encerramentos pendentes',
                        valor: encerramentoPendente,
                        cor: '#F97316',
                      },
                      { label: 'Sem dados', valor: semDados, cor: '#94A3B8' },
                    ]}
                    total={stat.total}
                  />
                  {/* Split ativas/inativas — info adicional alem do breakdown
                      por status (replica o estilo do card "Sem dados"). */}
                  {(ativas > 0 || inativas > 0) && (
                    <Flex
                      justify="space-between"
                      align="center"
                      pt={2}
                      mt={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <Text
                        fontSize="10px"
                        color="gray.500"
                        textTransform="uppercase"
                        fontWeight="600"
                      >
                        Por estado
                      </Text>
                      <Flex gap={1.5} align="baseline">
                        <Text as="span" color="#22C55E" fontWeight="700" fontSize="xs">
                          {ativas}
                        </Text>
                        <Text as="span" color="gray.500" fontSize="10px">
                          ativas
                        </Text>
                        <Text as="span" color="gray.300" fontSize="10px">
                          ·
                        </Text>
                        <Text as="span" color="#EF4444" fontWeight="700" fontSize="xs">
                          {inativas}
                        </Text>
                        <Text as="span" color="gray.500" fontSize="10px">
                          inativas
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                </>
              ) : (
                // Fallback minimo enquanto o backend antigo nao retorna o
                // breakdown granular. Mostra so o que esta disponivel no schema
                // legado (total / ok / pendentes / pct_ok) — evita "X de X com
                // fechamento" e linhas zeradas que confundiriam o leitor.
                <CardInfoBreakdown
                  descricao={
                    <>
                      <Text as="span" fontWeight="700">
                        {stat.total}
                      </Text>{' '}
                      empresas no regime{' '}
                      <Text as="span" fontWeight="700">
                        {stat.label}
                      </Text>
                      ,{' '}
                      <Text as="span" fontWeight="700" color={okColor}>
                        {formatPercent(stat.pct_ok)}
                      </Text>{' '}
                      em dia segundo o critério atual.
                    </>
                  }
                  linhas={[
                    { label: 'Em dia', valor: stat.ok, cor: '#22C55E' },
                    { label: 'Pendentes', valor: stat.pendentes, cor: PEND_COLOR },
                  ]}
                  total={stat.total}
                />
              )}
            </InfoPopover>
          </Box>
        </Flex>

        {/* Numero principal: com_dados/total neutro (mesmo padrao do card
            "Total" da Visao Geral). Sinal de saude vai no hint abaixo (verde
            em dia / vermelho pend.) e no popover (% em dia colorido).
            Schema antigo (sem `sem_dados`) cai num total simples — sem isso
            mostraria "X / X" sugerindo que todas tem fechamento. */}
        <Text
          fontSize={{ base: '28px', md: '32px' }}
          fontWeight="700"
          color="#0F172A"
          letterSpacing="-0.02em"
          lineHeight="1.05"
        >
          {hasNewSchema ? <ValueOverTotal value={comDados} total={stat.total} /> : stat.total}
        </Text>
        <Text fontSize="11px" color="#94A3B8" fontWeight="medium" mt={1}>
          empresas
        </Text>

        {/* Hint: X em dia · Y pend. (mesmo formato da Visao Geral) */}
        <Box mt={3}>
          {temAlgumStatus ? (
            <BreakdownHint okLabel="em dia" okValue={stat.ok} pendValue={stat.pendentes} />
          ) : (
            <Text fontSize="11px" color="#94A3B8">
              sem registros classificáveis
            </Text>
          )}
        </Box>
      </GlassCard>
    </motion.div>
  );
};

// ============================================================================
// Hero KPI (% em dia gigante)
// ============================================================================

type HeroScope = 'todas' | 'ativas' | 'inativas';

interface HeroData {
  pct: number;
  okCount: number;
  totalCount: number;
  pendentesLabel: string;
  pendentesValue: number;
  pendentesHint?: string;
  encerramentoValue: number;
  semDadosValue: number;
}

const buildHeroData = (t: IFechamentoTotalizadores, scope: HeroScope): HeroData => {
  if (scope === 'ativas') {
    return {
      pct: t.pct_ativas_ok,
      okCount: t.ativas_ok,
      totalCount: t.total_ativas,
      pendentesLabel: 'Pendentes',
      pendentesValue: t.ativas_pendentes,
      pendentesHint:
        t.media_dias_atraso_ativas !== null
          ? `atraso médio: ${formatDays(t.media_dias_atraso_ativas)}`
          : undefined,
      encerramentoValue: 0,
      semDadosValue: t.ativas_sem_dados,
    };
  }
  if (scope === 'inativas') {
    return {
      pct: t.pct_inativas_ok,
      okCount: t.inativas_ok,
      totalCount: t.total_inativas,
      pendentesLabel: 'Encerramentos pendentes',
      pendentesValue: t.inativas_pendentes,
      pendentesHint:
        t.media_dias_atraso_inativas !== null
          ? `atraso médio: ${formatDays(t.media_dias_atraso_inativas)}`
          : 'cliente saiu sem fechar',
      encerramentoValue: 0,
      semDadosValue: t.inativas_sem_dados,
    };
  }
  return {
    pct: t.pct_contabil_ok,
    okCount: t.contabil_ok,
    totalCount: t.total_empresas,
    pendentesLabel: 'Pendentes',
    pendentesValue: t.contabil_pendente,
    pendentesHint:
      t.media_dias_atraso_pendentes !== null
        ? `atraso médio: ${formatDays(t.media_dias_atraso_pendentes)}`
        : undefined,
    encerramentoValue: t.contabil_encerramento_pendente,
    semDadosValue: t.contabil_sem_dados,
  };
};

// Estatico — fora do render pra nao recriar o array a cada render do toggle.
const SCOPE_OPTIONS: { value: HeroScope; label: string }[] = [
  { value: 'todas', label: 'Todas' },
  { value: 'ativas', label: 'Ativas' },
  { value: 'inativas', label: 'Inativas' },
];

const ScopeToggle = ({
  value,
  onChange,
}: {
  value: HeroScope;
  // eslint-disable-next-line no-unused-vars
  onChange: (v: HeroScope) => void;
}) => {
  return (
    <Flex
      role="tablist"
      aria-label="Escopo de empresas"
      bg="rgba(15, 23, 42, 0.04)"
      borderRadius="full"
      p="3px"
      display="inline-flex"
      gap="2px"
    >
      {SCOPE_OPTIONS.map(opt => {
        const isActive = value === opt.value;
        return (
          <Box
            key={opt.value}
            as="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            px={3}
            py={1}
            borderRadius="full"
            fontSize="11px"
            fontWeight={isActive ? '700' : '500'}
            color={isActive ? '#0F172A' : '#71717A'}
            bg={isActive ? 'white' : 'transparent'}
            boxShadow={isActive ? '0 1px 4px -1px rgba(15, 23, 42, 0.12)' : undefined}
            transition="all 0.18s ease"
            lineHeight="1.4"
            _hover={!isActive ? { color: '#0F172A' } : undefined}
          >
            {opt.label}
          </Box>
        );
      })}
    </Flex>
  );
};

interface HeroKpiProps {
  t: IFechamentoTotalizadores;
  criterio: ICriterioFechamento;
  onOpenCriterio: () => void;
}

const HeroKpi = ({ t, criterio, onOpenCriterio }: HeroKpiProps) => {
  const [scope, setScope] = useState<HeroScope>('ativas');
  const data = buildHeroData(t, scope);

  let bigColor = '#EF4444';
  let bigGradient = 'linear-gradient(135deg, #DC2626 0%, #EF4444 50%, #F87171 100%)';
  if (data.pct >= 80) {
    bigColor = '#22C55E';
    bigGradient = 'linear-gradient(135deg, #16A34A 0%, #22C55E 50%, #4ADE80 100%)';
  } else if (data.pct >= 50) {
    bigColor = '#F59E0B';
    bigGradient = 'linear-gradient(135deg, #D97706 0%, #F59E0B 50%, #FBBF24 100%)';
  }

  const labelTopo =
    scope === 'inativas' ? 'Status contábil · encerradas' : 'Status contábil · em dia';
  const labelEmpresas = scope === 'inativas' ? 'inativas encerradas' : 'empresas em dia';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <GlassCard intensity="strong" p={{ base: 7, md: 10 }} position="relative">
        {/* Toggle + critério de cálculo — empilhados no canto superior direito.
            Ocupam o lugar onde antes vinha o card "Critério em uso" no topo
            da página, mantendo a tela mais enxuta. */}
        <Box position="absolute" top={{ base: 4, md: 6 }} right={{ base: 4, md: 6 }} zIndex={2}>
          <Stack spacing={2.5} align="flex-end">
            <ScopeToggle value={scope} onChange={setScope} />
            <CriterioCalculoCard criterio={criterio} onOpen={onOpenCriterio} />
          </Stack>
        </Box>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 6, md: 12 }}
          mt={{ base: 24, md: 16 }}
        >
          {/* Numero gigante */}
          <Box flexShrink={0}>
            <Text
              fontSize="11px"
              color="#71717A"
              textTransform="uppercase"
              letterSpacing="0.12em"
              fontWeight="semibold"
              mb={2}
            >
              {labelTopo}
            </Text>
            <Flex align="baseline" gap={2}>
              <Text
                fontSize={{ base: '76px', md: '120px' }}
                fontWeight="800"
                letterSpacing="-0.06em"
                lineHeight="0.92"
                sx={{
                  backgroundImage: bigGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {data.pct.toFixed(1)}
              </Text>
              <Text
                fontSize={{ base: '32px', md: '48px' }}
                fontWeight="600"
                color={bigColor}
                letterSpacing="-0.02em"
                lineHeight="1"
                opacity={0.55}
              >
                %
              </Text>
            </Flex>
            <Text fontSize="sm" color="#71717A" mt={2}>
              <Text as="span" fontWeight="600" color="#0F172A">
                {data.okCount}
              </Text>{' '}
              de {data.totalCount} {labelEmpresas}
            </Text>
          </Box>

          {/* Divisor sutil */}
          <Box
            display={{ base: 'none', md: 'block' }}
            w="1px"
            alignSelf="stretch"
            bg="rgba(15, 23, 42, 0.08)"
          />

          {/* Detalhes em coluna */}
          <Stack spacing={4} flex={1}>
            <DetailRow
              label={data.pendentesLabel}
              value={data.pendentesValue}
              hint={data.pendentesHint}
              color="#F59E0B"
            />
            {scope === 'todas' && (
              <DetailRow
                label="Encerramentos pendentes"
                value={data.encerramentoValue}
                hint="cliente saiu sem fechar"
                color="#EF4444"
              />
            )}
            <DetailRow
              label="Sem dados"
              value={data.semDadosValue}
              hint="NULL ou data inválida"
              color="#94A3B8"
            />
          </Stack>
        </Flex>
      </GlassCard>
    </motion.div>
  );
};

const DetailRow = ({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: number;
  hint?: string;
  color: string;
}) => (
  <Flex align="baseline" gap={4}>
    <Text
      fontSize="32px"
      fontWeight="700"
      color={color}
      letterSpacing="-0.02em"
      lineHeight="1"
      minW="80px"
    >
      {value}
    </Text>
    <Box>
      <Text fontSize="sm" fontWeight="600" color="#0F172A">
        {label}
      </Text>
      {hint && (
        <Text fontSize="xs" color="#94A3B8">
          {hint}
        </Text>
      )}
    </Box>
  </Flex>
);

// ============================================================================
// Section title
// ============================================================================

const SectionTitle = ({
  children,
  info,
  actions,
}: {
  children: React.ReactNode;
  info?: React.ReactNode;
  actions?: React.ReactNode;
}) => (
  <Flex align="center" gap={2} mb={6}>
    <Heading
      as="h2"
      fontSize={{ base: 'lg', md: 'xl' }}
      fontWeight="600"
      color="#0F172A"
      letterSpacing="-0.02em"
    >
      {children}
    </Heading>
    {info}
    {actions && <Box ml="auto">{actions}</Box>}
  </Flex>
);

// ============================================================================
// Picker de regimes — controla quais regimes entram nos KPIs e quais cards
// aparecem em "Por regime tributário". Persistido em localStorage via
// useRegimesIncluidos.
// ============================================================================

interface RegimesPickerProps {
  selecionados: RegimeChave[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (chave: RegimeChave) => void;
  onReset: () => void;
}

const RegimesPicker = ({ selecionados, onToggle, onReset }: RegimesPickerProps) => {
  const isCustom =
    selecionados.length !== REGIMES_INCLUIDOS_DEFAULT.length ||
    !REGIMES_INCLUIDOS_DEFAULT.every(c => selecionados.includes(c));

  return (
    <Popover placement="bottom-end" closeOnBlur>
      {/* IconButton e o trigger direto pra que aria-haspopup/aria-expanded
          caiam no elemento focavel — sem o wrapper Box, leitores de tela
          anunciam corretamente o estado expandido/colapsado. Bolinha azul
          via pseudo-elemento _after pra nao precisar de wrapper. */}
      <PopoverTrigger>
        <IconButton
          aria-label="Selecionar regimes considerados nos KPIs"
          icon={<SlidersHorizontal size={16} weight="bold" />}
          size="sm"
          variant="ghost"
          borderRadius="full"
          color="#475569"
          position="relative"
          _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
          _after={
            isCustom
              ? {
                  content: '""',
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '7px',
                  height: '7px',
                  borderRadius: 'full',
                  bg: '#1B88F5',
                  boxShadow: '0 0 0 2px white',
                  pointerEvents: 'none',
                }
              : undefined
          }
        />
      </PopoverTrigger>
      <PopoverContent w="280px" boxShadow="lg">
        <PopoverArrow />
        <PopoverHeader fontWeight="600" fontSize="sm" color="#0F172A" border="0" pb={1}>
          Regimes nos KPIs
        </PopoverHeader>
        <PopoverBody pt={0}>
          <Text fontSize="xs" color="#71717A" mb={3}>
            Empresas dos regimes não selecionados são excluídas de todos os indicadores
            (totalizadores, calendário e tabela).
          </Text>
          <Stack spacing={2}>
            {REGIMES_DISPONIVEIS.map(r => (
              <Checkbox
                key={r.chave}
                isChecked={selecionados.includes(r.chave)}
                onChange={() => onToggle(r.chave)}
                size="sm"
              >
                <Text fontSize="sm" color="#0F172A">
                  {r.label}
                </Text>
              </Checkbox>
            ))}
          </Stack>
        </PopoverBody>
        <PopoverFooter border="0" pt={2}>
          <Flex justify="space-between" align="center">
            <Text fontSize="11px" color="#94A3B8">
              {selecionados.length} de {REGIMES_DISPONIVEIS.length} selecionados
            </Text>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<ArrowClockwise size={12} />}
              onClick={onReset}
              isDisabled={!isCustom}
              color="#475569"
            >
              Padrão
            </Button>
          </Flex>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// Pagina
// ============================================================================

export function FechamentosPage() {
  const navigate = useNavigate();
  const { getTotalizadores } = useFechamentos();
  const { criterio, setCriterio } = useCriterioFechamento();
  const {
    regimes: regimesIncluidos,
    toggle: toggleRegimeIncluido,
    reset: resetRegimesIncluidos,
  } = useRegimesIncluidos();

  const [filters, setFilters] = useState<IFechamentoFilters>(DEFAULT_FILTERS);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(null);
  const [celulaSelecionada, setCelulaSelecionada] = useState<ICelulaSelecionada | null>(null);
  // "Apenas ativas" como filtro padrao da tela
  const [quickFilter, setQuickFilter] = useState<QuickFilterState>({
    estado: 'ativas',
    regime: null,
    semDados: false,
  });

  const filtersDrawer = useDisclosure();
  const detalheModal = useDisclosure();
  const criterioModal = useDisclosure();

  // Params do criterio (criterio + dias_lr/lp/sn/mei/outros) — passa em todas
  // as queries pra que o backend recompute `data_esperada` consistentemente.
  const criterioParams = useMemo(() => criterioToParams(criterio), [criterio]);
  const regimesParams = useMemo(
    () => regimesIncluidosToParams(regimesIncluidos),
    [regimesIncluidos],
  );

  const externalFilters = useMemo(
    () => ({ ...filtersToParams(filters), ...criterioParams, ...regimesParams }),
    [filters, criterioParams, regimesParams],
  );
  const activeFiltersCount = countActiveFilters(filters);

  // Limpa o filtro rapido por regime se o regime selecionado for desabilitado
  // no picker — senao a tela mostraria 0 empresas sem motivo aparente (filtro
  // rapido pedindo regime fora do universo configurado). Depende da `chave`
  // (string) em vez do objeto pra evitar re-execucao quando o `regime` muda
  // de referencia mas a chave e a mesma.
  const quickFilterRegimeChave = quickFilter.regime?.chave;
  useEffect(() => {
    if (quickFilterRegimeChave && !regimesIncluidos.includes(quickFilterRegimeChave)) {
      setQuickFilter(prev => ({ ...prev, regime: null }));
    }
  }, [regimesIncluidos, quickFilterRegimeChave]);

  const toggleEstado = (e: NonNullable<EstadoFiltro>) => {
    setQuickFilter(prev => ({
      ...prev,
      estado: prev.estado === e ? null : e,
    }));
    // Drawer tem `apenas_ativas` que tambem aplica `ativa=Ativa` no backend.
    // Se o user clicou "inativas", quickFilter vence (viewFilters da delete em
    // `ativa`) — mas o checkbox no drawer ficaria marcado sem efeito. Limpa o
    // checkbox sincronamente pra UI nao mentir.
    if (e === 'inativas') {
      setFilters(prev => (prev.apenas_ativas ? { ...prev, apenas_ativas: false } : prev));
    }
  };

  const toggleRegime = (chave: RegimeChave, valor: string) => {
    setQuickFilter(prev => ({
      ...prev,
      regime: prev.regime?.chave === chave ? null : { chave, valor },
    }));
  };

  const toggleSemDados = () => {
    setQuickFilter(prev => ({ ...prev, semDados: !prev.semDados }));
  };

  // Filtros que vão pra calendario+tabela: drawer + quickFilter + celula calendario.
  // Os totalizadores NAO recebem o quickFilter (mantem visao geral).
  const viewFilters = useMemo(() => {
    const out: Record<string, string | number | boolean> = { ...externalFilters };
    if (quickFilter.estado === 'ativas') {
      out.ativa = 'Ativa';
      delete out.apenas_inativas;
    } else if (quickFilter.estado === 'inativas') {
      out.apenas_inativas = 'true';
      delete out.ativa;
    }
    if (quickFilter.regime) {
      // "Outros" e categoria sintetica (nao bate com valor do banco) — usa
      // o flag regime_outros que o backend traduz em filtro por exclusao.
      if (quickFilter.regime.chave === 'outros') {
        out.regime_outros = 'true';
      } else {
        out.tipo_regime = quickFilter.regime.valor;
      }
    }
    if (quickFilter.semDados) {
      out.apenas_sem_dados = 'true';
    }
    return out;
  }, [externalFilters, quickFilter]);

  // Limpa a celula selecionada quando viewFilters muda. Sem isso, a tabela
  // continuaria filtrando "Mar/2024" mesmo com user trocando KPI/regime, e o
  // calendario (que usa viewFilters direto) nao concordaria com a tabela.
  useEffect(() => {
    setCelulaSelecionada(null);
  }, [viewFilters]);

  const tableFilters = useMemo(() => {
    if (!celulaSelecionada) return viewFilters;
    const { ano, mes } = celulaSelecionada;
    const mesStr = String(mes).padStart(2, '0');
    return {
      ...viewFilters,
      ano_mes_filtro: `${ano}-${mesStr}`,
    };
  }, [viewFilters, celulaSelecionada]);

  const totalizadoresQuery = useQuery({
    queryKey: ['fechamento-totalizadores', externalFilters],
    queryFn: getTotalizadores,
    staleTime: 2 * 60 * 1000,
    // Mantem KPIs anteriores visiveis durante refetch (evita flicker ao trocar
    // filtro rapido/scope toggle/calendario).
    placeholderData: prev => prev,
  });

  const t: IFechamentoTotalizadores | undefined = totalizadoresQuery.data?.data;

  const openEmpresaDetalhe = (empresaId: number) => {
    setSelectedEmpresaId(empresaId);
    detalheModal.onOpen();
  };

  return (
    <GlassPageBg>
      <Box
        maxW="1280px"
        mx="auto"
        w="full"
        px={{ base: 4, md: 8 }}
        py={{ base: 6, md: 10 }}
        position="relative"
        zIndex={1}
      >
        {/* === Breadcrumb + filtros === */}
        <Flex align="center" justify="space-between" gap={4} mb={6} flexWrap="wrap">
          <Flex align="center" gap={3}>
            <Box
              as="button"
              onClick={() => navigate('/modulesHome/accountingConciliation')}
              aria-label="Voltar"
              p={2}
              borderRadius="lg"
              transition="all 0.2s ease"
              _hover={{ bg: 'rgba(27, 136, 245, 0.08)' }}
            >
              <ArrowLeft size={18} color="#0F172A" />
            </Box>
            <Text color="#94A3B8" fontSize="sm" fontWeight="medium">
              Conciliação Contábil &nbsp;›&nbsp;{' '}
              <Text as="span" color="#0F172A" fontWeight="600">
                Fechamentos
              </Text>
            </Text>
          </Flex>

          <Button
            leftIcon={<FunnelSimple size={16} />}
            variant={activeFiltersCount > 0 ? 'solid' : 'outline'}
            colorScheme="blue"
            size="sm"
            onClick={filtersDrawer.onOpen}
            borderRadius="full"
            px={5}
          >
            Filtros
            {activeFiltersCount > 0 && (
              <Badge ml={2} colorScheme="whiteAlpha" variant="solid" borderRadius="full">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </Flex>

        {/* === Hero === */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Heading
            as="h1"
            fontSize={{ base: '26px', md: '32px' }}
            fontWeight="700"
            color="#0F172A"
            letterSpacing="-0.02em"
            lineHeight="1.15"
            mb={2}
          >
            Acompanhamento de fechamentos
          </Heading>
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            color="#71717A"
            maxW="680px"
            mb={10}
            lineHeight="1.55"
          >
            Status do fechamento contábil por empresa, com base em cliente desde / até.
          </Text>
        </motion.div>

        {/* === Loading / Error === */}
        {totalizadoresQuery.isLoading && (
          <Flex justify="center" py={20}>
            <Spinner size="lg" color="#1B88F5" thickness="3px" />
          </Flex>
        )}

        {totalizadoresQuery.isError && (
          <GlassCard intensity="medium" p={5} mb={6}>
            <Flex align="center" gap={3}>
              <Warning size={22} color="#C53030" />
              <Text color="#C53030" fontSize="sm" fontWeight="medium">
                Erro ao carregar totalizadores. Verifique se a API está respondendo.
              </Text>
            </Flex>
          </GlassCard>
        )}

        {t && (
          <Stack spacing={{ base: 10, md: 14 }}>
            {/* Hero KPI — ja inclui o critério de cálculo embutido no canto superior */}
            <HeroKpi t={t} criterio={criterio} onOpenCriterio={criterioModal.onOpen} />

            {/* Visao geral */}
            <Box>
              <ScrollFade>
                <SectionTitle
                  info={
                    <InfoPopover title="Visão geral">
                      Composição da carteira de clientes (ativos + inativos), filtrada pelos filtros
                      aplicados acima.
                    </InfoPopover>
                  }
                >
                  Visão geral
                </SectionTitle>
              </ScrollFade>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {(() => {
                  // "Com dados" = total da categoria menos as sem_dados.
                  // O numero grande do card mostra COM_DADOS / TOTAL — assim
                  // fica claro qual fatia da carteira tem informacao classificavel.
                  const totalComDados = t.total_empresas - t.contabil_sem_dados;
                  const ativasComDados = t.total_ativas - t.ativas_sem_dados;
                  const inativasComDados = t.total_inativas - t.inativas_sem_dados;
                  return (
                    <>
                      <StatTile
                        label="Total"
                        value={<ValueOverTotal value={totalComDados} total={t.total_empresas} />}
                        hint={
                          <BreakdownHint
                            okLabel="em dia"
                            okValue={t.contabil_ok}
                            pendValue={t.contabil_pendente + t.contabil_encerramento_pendente}
                          />
                        }
                        delay={0}
                        info={{
                          title: 'Total de empresas',
                          content: (
                            <CardInfoBreakdown
                              descricao={
                                <>
                                  <Text as="span" fontWeight="700">
                                    {totalComDados}
                                  </Text>{' '}
                                  de{' '}
                                  <Text as="span" fontWeight="700">
                                    {t.total_empresas}
                                  </Text>{' '}
                                  empresas têm fechamento contábil registrado. Distribuição segundo
                                  o critério atual:
                                </>
                              }
                              linhas={[
                                { label: 'Em dia', valor: t.contabil_ok, cor: '#22C55E' },
                                {
                                  label: 'Pendentes (ativas)',
                                  valor: t.contabil_pendente,
                                  cor: PEND_COLOR,
                                },
                                {
                                  label: 'Encerramentos pendentes',
                                  valor: t.contabil_encerramento_pendente,
                                  cor: '#F97316',
                                },
                                {
                                  label: 'Sem dados',
                                  valor: t.contabil_sem_dados,
                                  cor: '#94A3B8',
                                },
                              ]}
                              total={t.total_empresas}
                            />
                          ),
                        }}
                      />
                      <StatTile
                        label="Ativas"
                        value={
                          <ValueOverTotal
                            value={ativasComDados}
                            total={t.total_ativas}
                            color="#22C55E"
                          />
                        }
                        hint={
                          <BreakdownHint
                            okLabel="em dia"
                            okValue={t.ativas_ok}
                            pendValue={t.ativas_pendentes}
                          />
                        }
                        delay={0.07}
                        isSelected={quickFilter.estado === 'ativas'}
                        onClick={() => toggleEstado('ativas')}
                        info={{
                          title: 'Empresas ativas',
                          content: (
                            <CardInfoBreakdown
                              descricao={
                                <>
                                  <Text as="span" fontWeight="700">
                                    {ativasComDados}
                                  </Text>{' '}
                                  de{' '}
                                  <Text as="span" fontWeight="700">
                                    {t.total_ativas}
                                  </Text>{' '}
                                  ativas têm fechamento registrado. As outras{' '}
                                  <Text as="span" fontWeight="700">
                                    {t.ativas_sem_dados}
                                  </Text>{' '}
                                  caem em &quot;sem dados&quot;. Clique no card pra filtrar.
                                </>
                              }
                              linhas={[
                                { label: 'Em dia', valor: t.ativas_ok, cor: '#22C55E' },
                                {
                                  label: 'Pendentes',
                                  valor: t.ativas_pendentes,
                                  cor: PEND_COLOR,
                                },
                                {
                                  label: 'Sem dados',
                                  valor: t.ativas_sem_dados,
                                  cor: '#94A3B8',
                                },
                              ]}
                              total={t.total_ativas}
                            />
                          ),
                        }}
                      />
                      <StatTile
                        label="Inativas"
                        value={
                          <ValueOverTotal
                            value={inativasComDados}
                            total={t.total_inativas}
                            color="#EF4444"
                          />
                        }
                        hint={
                          <BreakdownHint
                            okLabel="encerradas"
                            okValue={t.inativas_ok}
                            pendValue={t.inativas_pendentes}
                          />
                        }
                        delay={0.14}
                        isSelected={quickFilter.estado === 'inativas'}
                        onClick={() => toggleEstado('inativas')}
                        info={{
                          title: 'Empresas inativas',
                          content: (
                            <CardInfoBreakdown
                              descricao={
                                <>
                                  <Text as="span" fontWeight="700">
                                    {inativasComDados}
                                  </Text>{' '}
                                  de{' '}
                                  <Text as="span" fontWeight="700">
                                    {t.total_inativas}
                                  </Text>{' '}
                                  inativas têm fechamento registrado. Inativas são empresas com data
                                  de saída (dt_cli_ate) preenchida.
                                </>
                              }
                              linhas={[
                                {
                                  label: 'Encerradas (fecharam até a saída)',
                                  valor: t.inativas_ok,
                                  cor: '#22C55E',
                                },
                                {
                                  label: 'Encerramentos pendentes',
                                  valor: t.inativas_pendentes,
                                  cor: PEND_COLOR,
                                },
                                {
                                  label: 'Sem dados',
                                  valor: t.inativas_sem_dados,
                                  cor: '#94A3B8',
                                },
                              ]}
                              total={t.total_inativas}
                            />
                          ),
                        }}
                      />
                      <StatTile
                        label="Sem dados"
                        value={
                          <ValueOverTotal
                            value={t.contabil_sem_dados}
                            total={t.total_empresas}
                            color="#94A3B8"
                          />
                        }
                        hint={
                          <Flex gap={1.5} align="baseline" flexWrap="wrap">
                            <Text as="span" color="#22C55E" fontWeight="600">
                              {t.ativas_sem_dados} ativas
                            </Text>
                            <Text as="span" color="#94A3B8" fontSize="11px">
                              ·
                            </Text>
                            <Text as="span" color="#EF4444" fontWeight="600">
                              {t.inativas_sem_dados} inativas
                            </Text>
                          </Flex>
                        }
                        delay={0.21}
                        isSelected={quickFilter.semDados}
                        onClick={toggleSemDados}
                        info={{
                          title: 'Sem dados',
                          content: (
                            <CardInfoBreakdown
                              descricao={
                                <>
                                  <Text as="span" fontWeight="700">
                                    {t.contabil_sem_dados}
                                  </Text>{' '}
                                  de{' '}
                                  <Text as="span" fontWeight="700">
                                    {t.total_empresas}
                                  </Text>{' '}
                                  empresas sem fechamento contábil registrado: nunca fecharam (NULL
                                  no banco), têm data sentinela do Domínio (&gt; 10 anos) ou são
                                  inativas sem data de saída. Clique pra filtrar.
                                </>
                              }
                              linhas={[
                                {
                                  label: 'Sem dados — ativas',
                                  valor: t.ativas_sem_dados,
                                  cor: '#22C55E',
                                },
                                {
                                  label: 'Sem dados — inativas',
                                  valor: t.inativas_sem_dados,
                                  cor: '#EF4444',
                                },
                              ]}
                              total={t.total_empresas}
                            />
                          ),
                        }}
                      />
                    </>
                  );
                })()}
              </SimpleGrid>
            </Box>

            {/* Por regime */}
            <Box>
              <ScrollFade>
                <SectionTitle
                  info={
                    <InfoPopover title="Por regime tributário">
                      Distribuição por regime: Lucro Real, Lucro Presumido, Simples Nacional, MEI e
                      Outros (Domésticas, Imune/Isenta etc). Cada card mostra o total de empresas, %
                      em dia e quantos pendentes.
                    </InfoPopover>
                  }
                  actions={
                    <RegimesPicker
                      selecionados={regimesIncluidos}
                      onToggle={toggleRegimeIncluido}
                      onReset={resetRegimesIncluidos}
                    />
                  }
                >
                  Por regime tributário
                </SectionTitle>
              </ScrollFade>
              {(() => {
                // Itera a partir de regimesIncluidos (ordem canonica do hook,
                // mesma ordem de REGIMES_DISPONIVEIS) e busca cada stat no
                // array do backend — defensivo contra mudanca de ordem da
                // resposta e contra regime ausente (ex: bucket novo ainda
                // nao classificavel pelo backend antigo).
                const visiveis = regimesIncluidos
                  .map(c => t.por_regime.find(s => s.chave === c))
                  .filter((s): s is IRegimeStats => !!s);
                if (visiveis.length === 0) {
                  return (
                    <GlassCard intensity="medium" p={5}>
                      <Text fontSize="sm" color="#71717A">
                        Nenhum regime selecionado. Use o seletor à direita do título para escolher
                        os regimes considerados nos KPIs.
                      </Text>
                    </GlassCard>
                  );
                }
                return (
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                    {visiveis.map((stat, idx) => {
                      const isSelected = quickFilter.regime?.chave === stat.chave;
                      return (
                        <RegimeCard
                          key={stat.chave}
                          stat={stat}
                          delay={idx * 0.06}
                          isSelected={isSelected}
                          onClick={() => toggleRegime(stat.chave, stat.label)}
                        />
                      );
                    })}
                  </SimpleGrid>
                );
              })()}
            </Box>

            {/* Banner quick filter ativo */}
            {hasAnyQuickFilter(quickFilter) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <GlassCard intensity="medium" p={4} tint="#1B88F5">
                  <Flex align="center" justify="space-between" gap={3}>
                    <Flex align="center" gap={2} flexWrap="wrap">
                      <Badge colorScheme="blue" fontSize="xs" borderRadius="full" px={3}>
                        Filtros rápidos
                      </Badge>
                      {quickFilter.estado && (
                        <Badge
                          colorScheme="blue"
                          variant="subtle"
                          fontSize="xs"
                          borderRadius="full"
                          px={3}
                          py={1}
                          cursor="pointer"
                          onClick={() => toggleEstado(quickFilter.estado!)}
                          _hover={{ opacity: 0.8 }}
                        >
                          {labelEstado(quickFilter.estado)} ✕
                        </Badge>
                      )}
                      {quickFilter.regime && (
                        <Badge
                          colorScheme="purple"
                          variant="subtle"
                          fontSize="xs"
                          borderRadius="full"
                          px={3}
                          py={1}
                          cursor="pointer"
                          onClick={() =>
                            toggleRegime(quickFilter.regime!.chave, quickFilter.regime!.valor)
                          }
                          _hover={{ opacity: 0.8 }}
                        >
                          {quickFilter.regime.valor} ✕
                        </Badge>
                      )}
                      {quickFilter.semDados && (
                        <Badge
                          colorScheme="gray"
                          variant="subtle"
                          fontSize="xs"
                          borderRadius="full"
                          px={3}
                          py={1}
                          cursor="pointer"
                          onClick={toggleSemDados}
                          _hover={{ opacity: 0.8 }}
                        >
                          Sem dados ✕
                        </Badge>
                      )}
                    </Flex>
                    <CloseButton
                      size="sm"
                      aria-label="Limpar todos os filtros rápidos"
                      onClick={() => setQuickFilter(EMPTY_QUICK_FILTER)}
                    />
                  </Flex>
                </GlassCard>
              </motion.div>
            )}

            {/* Calendario */}
            <ScrollFade>
              <CalendarioFechamentos
                externalFilters={viewFilters}
                selectedCelula={celulaSelecionada}
                onSelectCelula={setCelulaSelecionada}
              />
            </ScrollFade>

            {/* Banner de filtro de calendario ativo */}
            {celulaSelecionada && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <GlassCard intensity="medium" p={4} tint="#1B88F5">
                  <Flex align="center" justify="space-between" gap={3}>
                    <Flex align="center" gap={3} flexWrap="wrap">
                      <Badge colorScheme="blue" fontSize="xs" borderRadius="full" px={3}>
                        Filtro ativo
                      </Badge>
                      <Text fontSize="sm" color="#0F172A">
                        Empresas com último fechamento em{' '}
                        <Text as="span" fontWeight="700">
                          {MESES_FULL[celulaSelecionada.mes - 1]} de {celulaSelecionada.ano}
                        </Text>{' '}
                        ·{' '}
                        <Text as="span" color="#71717A">
                          {celulaSelecionada.qtd} empresa{celulaSelecionada.qtd > 1 ? 's' : ''}
                        </Text>
                      </Text>
                    </Flex>
                    <CloseButton
                      size="sm"
                      aria-label="Limpar filtro do calendário"
                      onClick={() => setCelulaSelecionada(null)}
                    />
                  </Flex>
                </GlassCard>
              </motion.div>
            )}

            {/* Tabela */}
            <ScrollFade>
              <EmpresasFechamentoTable
                externalFilters={tableFilters}
                onSelectEmpresa={emp => openEmpresaDetalhe(emp.id)}
              />
            </ScrollFade>
          </Stack>
        )}
      </Box>

      <FiltersDrawer
        isOpen={filtersDrawer.isOpen}
        onClose={filtersDrawer.onClose}
        filters={filters}
        onApply={setFilters}
      />

      <EmpresaFechamentoModal
        empresaId={selectedEmpresaId}
        isOpen={detalheModal.isOpen}
        onClose={detalheModal.onClose}
        criterioParams={criterioParams}
      />

      <CriterioCalculoModal
        isOpen={criterioModal.isOpen}
        onClose={criterioModal.onClose}
        current={criterio}
        onApply={setCriterio}
      />
    </GlassPageBg>
  );
}
